import React, { useEffect, useState } from 'react';
import { css, Theme } from '@emotion/react';
import { ContextMenu, contextStyles } from '@birdi/context-menu/ContextMenu';
import { BirdiTheme } from '@birdi/theme';
import { useLocation } from 'react-router';
import RenameSVG from '@birdi/icons/rename.svg';
import TrashSVG from '@birdi/icons/trash-2.svg';
import DropDownSVG from '@birdi/icons/more-horizontal.svg';
import { SidebarLink, iconStyle } from '../side-bar-utils';
import FolderSVG from '@birdi/icons/folder.svg';
import { ItemInstance } from '@headless-tree/core';

interface Folder {
  id: string;
  name: string;
}

interface FolderButtonProps {
  folder: Folder;
  item?: ItemInstance<string>;
  onDelete: (folder: Folder) => void;
  onRename: (folder: Folder) => void;
}

interface FolderMenuTriggerProps {
  toggle: () => void;
  toggleRef: React.RefObject<HTMLButtonElement>;
  open?: boolean;
  onOpenChange: (open: boolean) => void;
}

function FolderMenuTrigger({
  toggle,
  toggleRef,
  open = false,
  onOpenChange,
}: FolderMenuTriggerProps) {
  useEffect(() => {
    onOpenChange(open);
  }, [open, onOpenChange]);

  return (
    <button
      type="button"
      ref={toggleRef}
      aria-label="Folder actions"
      css={(theme) => css`
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        width: 1.75rem;
        height: 1.75rem;
        &:hover {
          background: ${theme.sand};
        }
      `}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        toggle();
      }}
    >
      <DropDownSVG color="#777" />
    </button>
  );
}

export function FolderButton({
  folder,
  item,
  onDelete,
  onRename,
}: FolderButtonProps) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const rawItemProps = item?.getProps() ?? {};
  const {
    onDragStart: treeOnDragStart,
    ...itemProps
  } = rawItemProps as typeof rawItemProps & {
    onDragStart?: (event: React.DragEvent<HTMLDivElement>) => void;
  };
  const isDragTarget = item?.isDragTarget() ?? false;
  const isDraggingOver = item?.isDraggingOver() ?? false;

  const renameFolder = () => {
    onRename(folder);
  };

  const deleteFolder = () => {
    onDelete(folder);
  };

  return (
    <div
      {...itemProps}
      data-menu-open={menuOpen}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', folder.id);
        treeOnDragStart?.(event);
      }}
      css={(theme: BirdiTheme) => css`
        position: relative;
        z-index: ${menuOpen ? 20 : 0};
        background: ${isDragTarget || isDraggingOver ? theme.lightOcean : 'transparent'};
        border-radius: 7px;
        user-select: none;

        &[draggable='true'] {
          cursor: grab;
        }

        &[draggable='true']:active {
          cursor: grabbing;
        }

        &:hover .folder-actions,
        &[data-menu-open='true'] .folder-actions {
          opacity: 1;
        }

        a,
        a * {
          -webkit-user-drag: none;
        }
      `}
    >
      <SidebarLink
        to={`/new-maps/folder/${folder.id}`}
        match={location.pathname}
        dashboard
        extraStyles={css`
          padding-top: 0.25em;
          padding-bottom: 0.25em;
          -webkit-user-drag: none;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 0.5em;
            width: 100%;
            min-width: 0;
          `}
        >
          <FolderSVG css={iconStyle} />
          <span
            css={css`
              flex: 1;
              min-width: 0;
              line-height: 1.25;
            `}
          >
            {folder.name}
          </span>
        </div>
      </SidebarLink>
      <div
        className="folder-actions"
        css={(theme: BirdiTheme) => css`
          position: absolute;
          top: 50%;
          right: 0.5rem;
          transform: translateY(-50%);
          opacity: 0;
          transition: opacity 0.2s ease;
          cursor: pointer;
          border-radius: 4px;
          color: ${theme.midnight};
        `}
      >
        <ContextMenu
          alignRight
          ButtonComponent={(props) => (
            <FolderMenuTrigger {...props} onOpenChange={setMenuOpen} />
          )}
        >
          {({ hide }) => (
            <div
              css={(theme: BirdiTheme) => css`
                ${contextStyles(theme)};
                min-width: 170px;

                button {
                  align-items: center;
                  gap: 0.5em;

                  svg {
                    flex-shrink: 0;
                    width: 1em;
                    height: 1em;
                  }
                }
              `}
            >
              <button
                type="button"
                onClick={() => {
                  renameFolder();
                  hide();
                }}
              >
                <RenameSVG />
                <span>Rename folder</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  deleteFolder();
                  hide();
                }}
              >
                <TrashSVG
                  css={(theme: Theme) => css`
                    color: ${theme.midnight};
                  `}
                />
                <span>Delete folder</span>
              </button>
            </div>
          )}
        </ContextMenu>
      </div>
    </div>
  );
}
